// The codes the integration answers with, as a coordinator reads them. Codes
// never carry personal data; anything unknown falls back to a generic sentence.

/* eslint-disable camelcase */
const EMPLOYEE_REASONS: Record<string, string> = {
  missing_job: 'Sin cargo en Odoo',
  unmapped_job: 'Cargo sin mapear',
  missing_rut: 'Sin RUT en Odoo',
  invalid_rut: 'RUT inválido',
  duplicate_rut: 'RUT repetido en otro empleado',
  rut_taken: 'RUT de un profesional vinculado a otro empleado',
  archived_with_future_shifts: 'Archivado en Odoo con turnos futuros en Kuido',
  invalid_caregiver: 'Datos que Kuido no acepta',
  sync_error: 'No se pudo leer en la última sincronización',
};

const SESSION_CODES: Record<string, string> = {
  no_service_line: 'Sin línea de presupuesto para la fecha',
  ambiguous_service_line: 'Más de una línea posible',
  picked_line_invalid: 'La línea elegida ya no cubre el turno',
  caregiver_not_linked: 'Profesional sin vínculo con Odoo',
  missing_caregiver_cost: 'Turno sin pago al profesional',
  service_line_without_project: 'Línea sin proyecto en Odoo',
  duplicate_session: 'Dos sesiones con la misma referencia en Odoo',
  session_taken: 'La sesión pertenece a otro turno',
  session_locked: 'Sesión validada o facturada en Odoo',
  orphan_session: 'Sesión de Kuido sin turno',
  unexpected_error: 'Error inesperado al enviar',
};

const ACTION_ERRORS: Record<string, string> = {
  order_dismissed: 'El presupuesto fue descartado.',
  unknown_orders: 'Uno de los presupuestos ya no está disponible. Recarga la página.',
  orders_of_other_patients: 'Los presupuestos son de pacientes distintos.',
  unknown_patient: 'No tienes acceso a ese paciente.',
  go_live_required: 'Indica desde qué fecha se incorpora.',
  go_live_in_past: 'La fecha de incorporación no puede ser pasada.',
  go_live_too_early: 'La fecha no puede ser anterior al inicio del presupuesto ni a este mes.',
  invalid_go_live: 'La fecha de incorporación no es válida.',
  not_incorporated: 'Este paciente aún no está incorporado.',
  go_live_started: 'Kuido ya envía las sesiones de este paciente: la fecha no se puede cambiar.',
  duplicate_lines: 'Una línea quedó en dos requerimientos.',
  unknown_lines: 'Una de las líneas ya no está disponible. Recarga la página.',
  lines_unaccounted: 'Cada línea debe quedar en un requerimiento o descartada.',
  linked_line_dismissed: 'Una línea que ya está en un requerimiento no se puede descartar.',
  invalid_release: 'Solo se puede quitar una especialidad de una línea de paquete.',
  line_left_empty: 'Una línea de paquete debe quedar con al menos una especialidad.',
  unmapped_product: 'Una línea es de un producto sin mapear: descártala o pide mapearlo.',
  invalid_career: 'La especialidad no es válida.',
  unknown_requirement: 'Ese requerimiento ya no existe. Recarga la página.',
  group_required: 'Selecciona al menos un grupo para el paciente.',
  rut_required: 'Indica el RUT del paciente.',
  rut_taken: 'Ya hay un paciente con este RUT en Kuido: elige «Paciente que ya está en Kuido».',
  overlapping_line: 'Dos líneas no pueden alimentar un requerimiento en las mismas fechas: ' +
    'crea un requerimiento aparte para la nueva.',
  line_ended: 'Esa línea ya terminó: sus valores no se pueden cambiar.',
  requirement_changed_twice: 'Dos líneas cambian el mismo requerimiento: cambia sus valores en una ' +
    'y deja la otra como está.',
  address_required: 'Indica la dirección principal del paciente.',
  invalid_rows: 'Los requerimientos enviados no son válidos.',
  order_linked: 'No se puede descartar el presupuesto de un paciente incorporado.',
  not_retryable: 'Esta sesión no tiene turno que enviar.',
  shift_gone: 'El turno ya no existe.',
  picked_line_invalid: 'Esa línea no cubre la fecha del turno.',
  connection_inactive: 'La conexión con Odoo está pausada.',
};
/* eslint-enable camelcase */

// Why the last synchronization failed, for a coordinator: what to expect.
/* eslint-disable camelcase */
const CONNECTION_ERRORS: Record<string, string> = {
  authentication_failed: 'Odoo rechazó las credenciales de la integración',
  http_401: 'Odoo rechazó las credenciales de la integración',
  http_403: 'Odoo no permite el acceso a la integración',
  timeout: 'Odoo no respondió a tiempo',
  connection_failed: 'No se pudo conectar con Odoo',
  server_error: 'Odoo tuvo un error interno',
};
/* eslint-enable camelcase */

export function connectionError(code: string): string {
  return CONNECTION_ERRORS[code] || `Error de Odoo (${code})`;
}

export function employeeReason(reason: string | null): string {
  return (reason && EMPLOYEE_REASONS[reason]) || 'Por revisar';
}

/* eslint-disable camelcase */
const SESSION_CONFLICTS: Record<string, string> = {
  outside_dates: 'Realizado fuera de las fechas del presupuesto',
};
/* eslint-enable camelcase */

// A Session Kuido keeps sending on its line although the line no longer fits.
export function sessionConflict(conflict: string): string {
  return SESSION_CONFLICTS[conflict] || 'Por revisar';
}

// Odoo's own refusals carry its exception name (validation_error, access_error…).
export function sessionCode(code: string | null): string {
  if (!code) return 'Sin error';

  return SESSION_CODES[code] || `Odoo rechazó la sesión (${code})`;
}

export function actionError(code: string | null, fallback: string): string {
  return (code && ACTION_ERRORS[code]) || fallback;
}
